import React, { useEffect, useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Wallet, Search, Loader2, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/utils/api';
import { showError } from '@/utils/toast';

const Loans = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    fetchLoans();
  }, [filterType, filterStatus]);

  const fetchLoans = async () => {
    try {
      const params = {};
      if (filterType) params.loan_type = filterType;
      if (filterStatus) params.status = filterStatus;
      const response = await api.get('/leads', { params });
      setLeads(response.data);
    } catch (error) {
      showError('Failed to fetch loans');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const map = {
      fresh: 'bg-blue-100 text-blue-800',
      follow_up: 'bg-orange-100 text-orange-800',
      login: 'bg-purple-100 text-purple-800',
      sanctioned: 'bg-green-100 text-green-800',
      disbursed: 'bg-emerald-100 text-emerald-800',
      rejected: 'bg-red-100 text-red-800',
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  // Total loan amount
  const totalAmount = leads.reduce((sum, l) => sum + (l.loan_amount || 0), 0);
  const disbursedAmount = leads.filter((l) => l.status === 'disbursed').reduce((sum, l) => sum + (l.loan_amount || 0), 0);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-primary" size={40} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div>
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Loans</h1>
          <p className="text-muted-foreground">Track all loan applications and disbursements</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-blue-50">
                <Wallet className="text-blue-600" size={24} />
              </div>
              <div>
                <p className="text-2xl font-bold">{leads.length}</p>
                <p className="text-xs text-muted-foreground">Total Applications</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-emerald-50">
                <TrendingUp className="text-emerald-600" size={24} />
              </div>
              <div>
                <p className="text-2xl font-bold">₹{(totalAmount / 100000).toFixed(1)}L</p>
                <p className="text-xs text-muted-foreground">Total Loan Volume</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-teal-50">
                <TrendingUp className="text-teal-600" size={24} />
              </div>
              <div>
                <p className="text-2xl font-bold">₹{(disbursedAmount / 100000).toFixed(1)}L</p>
                <p className="text-xs text-muted-foreground">Disbursed Volume</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <select
              data-testid="filter-loan-type"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none"
            >
              <option value="">All Loan Types</option>
              <option value="personal">Personal Loan</option>
              <option value="business">Business Loan</option>
              <option value="home">Home Loan</option>
              <option value="auto">Auto Loan</option>
            </select>
            <select
              data-testid="filter-loan-status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none"
            >
              <option value="">All Status</option>
              <option value="fresh">Fresh</option>
              <option value="follow_up">Follow Up</option>
              <option value="login">Login</option>
              <option value="sanctioned">Sanctioned</option>
              <option value="disbursed">Disbursed</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted border-b border-border">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase">Customer</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase">Loan Type</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase">Amount</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase">Income</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {leads.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-muted-foreground">No loans found</td>
                  </tr>
                ) : (
                  leads.map((l) => (
                    <tr key={l.id} className="hover:bg-accent/50 transition-colors">
                      <td className="px-6 py-4">
                        <Link to={`/leads/${l.id}`} className="font-medium text-foreground hover:text-primary">{l.customer_name}</Link>
                        <p className="text-xs text-muted-foreground">{l.mobile_number}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground capitalize">{l.loan_type}</td>
                      <td className="px-6 py-4 text-sm font-medium text-foreground">
                        {l.loan_amount ? `₹${l.loan_amount.toLocaleString('en-IN')}` : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground">
                        {l.monthly_income ? `₹${l.monthly_income.toLocaleString('en-IN')}/mo` : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(l.status)}`}>
                          {l.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Loans;
